import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import SEOMetaTags from '../components/SEOMetaTags';
import { trackPageView, trackButtonClick } from '../analytics';

const UnlockPdf = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [unlockInfo, setUnlockInfo] = useState(null);

  useEffect(() => {
    trackPageView('/unlock-pdf', 'Unlock PDF Page');
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        setError('Please select a PDF file');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }
      setSelectedFile(file);
      setError('');
      setUnlockInfo(null);
    }
  };

  const handleUnlock = async () => {
    if (!selectedFile) {
      setError('Please select a PDF file');
      return;
    }

    if (!password) {
      setError('Please enter the PDF password');
      return;
    }

    setIsLoading(true);
    setError('');
    setUnlockInfo(null);

    try {
      trackButtonClick('unlock_pdf', 'unlock_pdf');

      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('password', password);

      const response = await fetch('https://api.temp2doc.com/organise/unlock', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `Error: ${response.status} ${response.statusText}`);
      }

      // Get encryption info from headers
      const encryptionType = response.headers.get('X-Encryption-Type');
      const passwordUsed = response.headers.get('X-Password-Used');

      // Get the unlocked PDF
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `unlocked_${selectedFile.name}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      // Show info about what was unlocked
      let infoMessage = '';
      if (encryptionType === 'none') {
        infoMessage = 'No encryption detected - file copied as-is';
      } else if (encryptionType === 'permission_only') {
        infoMessage = 'Permission-only lock removed (no password required)';
      } else if (encryptionType === 'user_password') {
        infoMessage = 'User password encryption removed successfully';
      }

      setUnlockInfo({
        type: encryptionType,
        passwordUsed: passwordUsed === 'true',
        message: infoMessage
      });

      trackButtonClick('unlock_pdf', 'unlock_success');
      setSelectedFile(null);
      setPassword('');
      
    } catch (err) {
      console.error('Unlock error:', err);
      setError(err.message || 'Failed to unlock PDF. Please try again.');
      trackButtonClick('unlock_pdf', 'unlock_error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <SEOMetaTags 
        title="Unlock PDF - Remove Password & Decrypt PDF Online Free | Temp2Doc"
        description="Unlock and decrypt password-protected PDFs online for free. Remove PDF passwords and restrictions instantly with 256-bit AES decryption support. Fast, secure, and easy to use."
        keywords="unlock PDF, remove PDF password, decrypt PDF, PDF password remover, unlock password protected PDF, remove PDF restrictions, PDF decryption, bypass PDF password, remove PDF security, PDF password recovery"
        canonicalUrl="https://temp2doc.com/unlock-pdf"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "Unlock PDF",
          "applicationCategory": "BusinessApplication",
          "operatingSystem": "Web browser",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD"
          },
          "description": "Unlock and decrypt password-protected PDFs instantly. Remove user passwords and owner restrictions with ease. Supports 256-bit AES encrypted PDFs.",
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.8",
            "ratingCount": "1150"
          },
          "featureList": [
            "Remove user password protection",
            "Remove owner password restrictions",
            "Supports 256-bit AES encryption",
            "Preserve original PDF quality",
            "Fast decryption processing",
            "No file size limits up to 10MB",
            "Secure processing with automatic file deletion",
            "No registration required",
            "Free online tool"
          ]
        }}
      />
      <Header />
      <main className="min-h-screen bg-gradient-to-b from-white to-blue-50 pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <section className="max-w-6xl mx-auto text-center mb-12">
          <div className="inline-block p-3 bg-primary-100 rounded-full mb-4">
            <svg className="w-12 h-12 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
            </svg>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Unlock PDF - Remove Password & Decrypt PDFs
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            Remove password protection from encrypted PDFs instantly. Enter the password to decrypt and unlock your PDF files.
          </p>
        </section>

        {/* Upload Section */}
        <section className="max-w-6xl mx-auto mb-12">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select PDF File (Max 10MB)
              </label>
              <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg hover:border-primary-500 transition-colors">
                <div className="space-y-1 text-center">
                  <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                    <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <div className="flex text-sm text-gray-600">
                    <label htmlFor="file-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-primary-600 hover:text-primary-500 focus-within:outline-none">
                      <span>Upload a file</span>
                      <input id="file-upload" name="file-upload" type="file" className="sr-only" accept=".pdf" onChange={handleFileChange} />
                    </label>
                    <p className="pl-1">or drag and drop</p>
                  </div>
                  <p className="text-xs text-gray-500">PDF up to 10MB</p>
                </div>
              </div>
              {selectedFile && (
                <p className="mt-2 text-sm text-gray-600">
                  Selected: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                </p>
              )}
            </div>

            {/* Password Field */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password <span className="text-red-500">*</span>
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter PDF password"
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              <p className="mt-1 text-xs text-gray-500">
                Enter the password to unlock this encrypted PDF file.
              </p>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {unlockInfo && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-600">
                  <strong>✓ Success:</strong> {unlockInfo.message}
                </p>
                <p className="text-xs text-green-500 mt-1">
                  Encryption type: {unlockInfo.type} | Password used: {unlockInfo.passwordUsed ? 'Yes' : 'No'}
                </p>
              </div>
            )}

            <button
              onClick={handleUnlock}
              disabled={!selectedFile || !password || isLoading}
              className="w-full bg-primary-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? 'Unlocking PDF...' : 'Unlock PDF'}
            </button>
          </div>
        </section>

        {/* How It Works */}
        <section className="max-w-6xl mx-auto mb-12">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">How It Works</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="flex items-center justify-center w-12 h-12 bg-primary-100 text-primary-600 rounded-full mb-4 font-bold text-lg">
                1
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Upload Your PDF</h3>
              <p className="text-gray-600 text-sm">
                Select the password-protected PDF file you want to unlock and decrypt.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="flex items-center justify-center w-12 h-12 bg-primary-100 text-primary-600 rounded-full mb-4 font-bold text-lg">
                2
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Enter Password</h3>
              <p className="text-gray-600 text-sm">
                Enter the password for your encrypted PDF file to remove all restrictions.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="flex items-center justify-center w-12 h-12 bg-primary-100 text-primary-600 rounded-full mb-4 font-bold text-lg">
                3
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Download Unlocked PDF</h3>
              <p className="text-gray-600 text-sm">
                Get your fully unlocked PDF with all restrictions removed instantly.
              </p>
            </div>
          </div>
        </section>

        {/* Key Features */}
        <section className="max-w-6xl mx-auto mb-12">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">Key Features</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="text-primary-600 mb-3">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Password Required</h3>
              <p className="text-gray-600 text-sm">
                Enter your PDF password to decrypt and remove all security restrictions.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="text-primary-600 mb-3">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Fast Processing</h3>
              <p className="text-gray-600 text-sm">
                Quickly remove all password protection and restrictions from your PDF files.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="text-primary-600 mb-3">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Secure & Private</h3>
              <p className="text-gray-600 text-sm">
                Your files are processed securely and automatically deleted after processing.
              </p>
            </div>
          </div>
        </section>

        {/* Related Tools Section */}
        <section className="max-w-6xl mx-auto">
          <nav className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">Related PDF Tools</h2>
          <ul className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <li>
              <a href="/chat-with-pdf" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                <span className="text-primary-600 font-medium">Chat PDF With AI</span>
              </a>
            </li>
            <li>
              <a href="/merge-pdf" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                <span className="text-primary-600 font-medium">Merge PDF</span>
              </a>
            </li>
            <li>
              <a href="/split-remove-rotate-pdf" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                <span className="text-primary-600 font-medium">Split Remove Rotate PDF</span>
              </a>
            </li>
            <li>
              <a href="/pdf-to-word" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                <span className="text-primary-600 font-medium">PDF to Word</span>
              </a>
            </li>
            <li>
              <a href="/pdf-to-excel" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                <span className="text-primary-600 font-medium">PDF to Excel</span>
              </a>
            </li>
            <li>
              <a href="/pdf-to-image" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                <span className="text-primary-600 font-medium">PDF to Image</span>
              </a>
            </li>
            <li>
              <a href="/pdf-to-powerpoint" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                <span className="text-primary-600 font-medium">PDF to PowerPoint</span>
              </a>
            </li>
            <li>
              <a href="/protect-pdf" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                <span className="text-primary-600 font-medium">Protect PDF</span>
              </a>
            </li>
            <li>
              <a href="/transform-pdf" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                <span className="text-primary-600 font-medium">Repair & Transform PDF</span>
              </a>
            </li>
            <li>
              <a href="/compress-pdf" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                <span className="text-primary-600 font-medium">Compress PDF</span>
              </a>
            </li>
            <li>
              <a href="/diagnose-pdf" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                <span className="text-primary-600 font-medium">Verify & Diagnose PDF</span>
              </a>
            </li>
            <li>
              <a href="/" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                <span className="text-primary-600 font-medium">Back to Home</span>
              </a>
            </li>
          </ul>
          </nav>
        </section>
      </main>
    </>
  );
};

export default UnlockPdf;
